<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import DashboardCard from '@/components/dashboard/DashboardCard.vue'
import MetricCard from '@/components/dashboard/MetricCard.vue'
import QueueRow from '@/components/dashboard/QueueRow.vue'
import QuickAction from '@/components/dashboard/QuickAction.vue'

const router = useRouter()
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const now = ref(new Date())

const storedUser = (() => {
  try {
    return JSON.parse(localStorage.getItem('user_data') || 'null')
  } catch {
    return null
  }
})()

const data = ref<any>({
  user: {
    name: storedUser?.name || localStorage.getItem('user_name') || 'Contractor',
    company_name: storedUser?.companyName || storedUser?.company || 'Independent Contractor',
    location: storedUser?.location || [storedUser?.city, storedUser?.state].filter(Boolean).join(', ') || 'North Dakota, USA',
  },
  stats: {
    active_jobs: 0,
    monthly_earnings: 0,
    pending_payments: 0,
    expiring_certifications: 0,
    open_field_tickets: 0,
    unpaid_invoices: 0,
    hours_this_week: 0,
    safety_score: 100,
  },
  today: null,
  dispatches: [],
  tickets: [],
  invoices: [],
  certifications: [],
  alerts: [],
  activity: [],
})

const stats = computed(() => data.value.stats || {})
const money = (v: any) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(v || 0))
const shortDate = computed(() => now.value.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }))
const go = (path: string) => router.push(path)

async function loadDashboard(spinner = true) {
  if (spinner) loading.value = true
  refreshing.value = !spinner
  error.value = ''
  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch('/api/user/dashboard', {
      headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    const text = await response.text()
    let payload: any = null
    try {
      payload = text ? JSON.parse(text) : null
    } catch {
      payload = null
    }
    if (!response.ok || !payload) {
      throw new Error(payload?.error || (response.status === 403
        ? 'This dashboard is for contractor accounts.'
        : 'Unable to load dashboard'))
    }
    const incomingStats = payload.stats || payload.dashboard_stats || {}
    data.value = {
      ...data.value,
      ...payload,
      user: { ...data.value.user, ...(payload.user || {}) },
      stats: {
        ...data.value.stats,
        ...incomingStats,
        active_jobs: incomingStats.active_jobs ?? payload.totalJobs ?? incomingStats.totalJobs ?? 0,
        monthly_earnings: incomingStats.monthly_earnings ?? payload.totalEarnings ?? incomingStats.totalEarnings ?? 0,
        pending_payments: incomingStats.pending_payments ?? payload.pendingPayments ?? incomingStats.pendingPayments ?? 0,
        expiring_certifications: incomingStats.expiring_certifications ?? payload.expiringCertifications ?? incomingStats.expiringCertifications ?? 0,
      },
      activity: payload.activity || payload.activities || data.value.activity,
    }
  } catch (e: any) {
    error.value = e?.message || 'Dashboard data could not be loaded.'
  } finally {
    loading.value = false
    refreshing.value = false
    now.value = new Date()
  }
}

onMounted(() => loadDashboard())
</script>

<template>
  <AppLayout page-title="Contractor Dashboard" page-subtitle="Jobs, tickets, invoices, and field work">
    <div class="mx-auto w-full max-w-[1600px]">
      <section class="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-700/50 bg-slate-800/45 p-4 shadow-2xl shadow-black/10 backdrop-blur-xl xl:flex-row xl:items-center xl:justify-between">
        <div class="flex min-w-0 items-center gap-3">
          <div class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-400 font-black text-slate-950">
            {{ (data.user?.name || 'C').split(' ').map((x: string) => x[0]).join('').slice(0, 2) }}
          </div>
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h1 class="truncate text-2xl font-extrabold tracking-tight text-white">{{ data.user?.name || 'Contractor' }}'s Dashboard</h1>
              <span class="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-300">1099 Contractor</span>
            </div>
            <p class="mt-1 truncate text-sm text-slate-400">{{ data.user?.company_name }} · {{ data.user?.location }}</p>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 text-sm">
          <div class="rounded-xl border border-slate-700/50 bg-slate-900/45 px-3 py-2"><span class="text-slate-500">Today</span><strong class="ml-2 text-white">{{ shortDate }}</strong></div>
          <div class="rounded-xl border border-slate-700/50 bg-slate-900/45 px-3 py-2"><span class="text-slate-500">Updated</span><strong class="ml-2 text-white">{{ now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) }}</strong></div>
          <button class="rounded-xl border border-slate-600/50 bg-slate-700/50 px-4 py-2 font-semibold hover:border-emerald-500/30 hover:text-emerald-300" :disabled="refreshing" @click="loadDashboard(false)">{{ refreshing ? 'Refreshing…' : '↻ Refresh' }}</button>
        </div>
      </section>

      <div v-if="error" class="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{{ error }}</div>

      <div v-if="loading" class="space-y-5">
        <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-5"><div v-for="n in 5" :key="n" class="h-32 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40"></div></div>
        <div class="grid gap-5 xl:grid-cols-12"><div class="h-80 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40 xl:col-span-7"></div><div class="h-80 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40 xl:col-span-5"></div></div>
      </div>

      <template v-else>
        <section class="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard title="Active Jobs" :value="stats.active_jobs ?? 0" subtitle="Currently working" icon="▣" tone="emerald" @click="go('/jobs')" />
          <MetricCard title="This Month" :value="money(stats.monthly_earnings)" subtitle="Total earnings" icon="$" tone="cyan" @click="go('/payments')" />
          <MetricCard title="Pending Payments" :value="stats.pending_payments ?? 0" subtitle="Awaiting approval" icon="◷" tone="amber" @click="go('/payments')" />
          <MetricCard title="Open Field Tickets" :value="stats.open_field_tickets ?? 0" subtitle="Need follow-up" icon="⌑" tone="purple" @click="go('/field-tickets')" />
          <MetricCard title="Certifications" :value="stats.expiring_certifications ?? 0" subtitle="Expiring soon" icon="!" tone="red" @click="go('/safety')" />
        </section>

        <section class="mb-5 grid gap-5 xl:grid-cols-12">
          <DashboardCard class="xl:col-span-5" title="Today / Now" subtitle="Your current field context" action-label="Open Schedule" @action="go('/scheduling')">
            <div v-if="data.today" class="space-y-3">
              <div class="rounded-xl border border-emerald-500/20 bg-emerald-500/[.07] p-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-xs font-semibold uppercase tracking-[.14em] text-emerald-300">Current Assignment</p>
                    <h3 class="mt-1 text-lg font-extrabold text-white">{{ data.today.job_title || data.today.well_name }}</h3>
                    <p class="mt-1 text-sm text-slate-400">{{ data.today.customer }} · {{ data.today.service_type }}</p>
                  </div>
                  <span class="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-bold uppercase text-emerald-300">{{ data.today.status || 'Scheduled' }}</span>
                </div>
                <div class="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div class="rounded-lg bg-slate-900/40 p-3"><span class="block text-xs text-slate-500">Location</span><strong class="mt-1 block text-slate-100">{{ data.today.location || '—' }}</strong></div>
                  <div class="rounded-lg bg-slate-900/40 p-3"><span class="block text-xs text-slate-500">Start</span><strong class="mt-1 block text-slate-100">{{ data.today.start_time || '—' }}</strong></div>
                </div>
              </div>
              <div class="grid grid-cols-3 gap-2">
                <button class="rounded-xl border border-slate-700/50 bg-slate-700/30 px-3 py-3 text-xs font-semibold" @click="go('/onsite')">On Site Hub</button>
                <button class="rounded-xl border border-slate-700/50 bg-slate-700/30 px-3 py-3 text-xs font-semibold" @click="go('/field-tickets')">Field Ticket</button>
                <button class="rounded-xl border border-slate-700/50 bg-slate-700/30 px-3 py-3 text-xs font-semibold" @click="go('/tank-watch')">Tank Watch</button>
              </div>
            </div>
            <div v-else class="grid min-h-52 place-items-center rounded-xl border border-dashed border-slate-700/60 bg-slate-900/25 p-6 text-center">
              <div>
                <div class="text-3xl">🛢️</div>
                <p class="mt-3 font-semibold text-white">No active assignment</p>
                <p class="mt-1 text-sm text-slate-500">Check the marketplace or your schedule for upcoming work.</p>
                <button class="mt-4 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-slate-950" @click="go('/jobs')">Find Jobs</button>
              </div>
            </div>
          </DashboardCard>

          <DashboardCard class="xl:col-span-4" title="Quick Actions" subtitle="Real next steps">
            <div class="grid grid-cols-2 gap-3">
              <QuickAction label="Find Jobs" icon="⌕" tone="cyan" @click="go('/jobs')" />
              <QuickAction label="Payroll" icon="$" tone="emerald" @click="go('/payroll')" />
              <QuickAction label="Create Invoice" icon="▤" tone="blue" @click="go('/invoices')" />
              <QuickAction label="On Site Hub" icon="▣" tone="emerald" @click="go('/onsite')" />
              <QuickAction label="Field Ticket" icon="⌑" tone="purple" @click="go('/field-tickets')" />
              <QuickAction label="Tank Watch" icon="◉" tone="red" @click="go('/tank-watch')" />
              <QuickAction label="Expense Entry" icon="$" tone="emerald" @click="go('/expenses')" />
              <QuickAction label="Safety Check" icon="✓" tone="amber" @click="go('/safety')" />
              <QuickAction label="Tax Estimate" icon="%" tone="cyan" @click="go('/tax-calculation')" />
              <QuickAction label="AI Assistant" icon="AI" tone="purple" @click="go('/ai-messaging')" />
            </div>
          </DashboardCard>

          <DashboardCard class="xl:col-span-3" title="Alerts" subtitle="Items that need attention" action-label="Safety" @action="go('/safety')">
            <div v-if="data.alerts?.length" class="space-y-2">
              <QueueRow v-for="(item, i) in data.alerts.slice(0, 5)" :key="item.id || i" :title="item.title || 'Alert'" :subtitle="item.message || item.description || ''" :meta="item.time || item.created_at || ''" :tone="item.severity === 'high' ? 'red' : item.severity === 'warning' ? 'amber' : 'cyan'" />
            </div>
            <div v-else class="grid min-h-52 place-items-center rounded-xl bg-slate-900/25 p-5 text-center">
              <div><div class="text-2xl">✓</div><p class="mt-2 font-semibold text-white">All caught up</p><p class="mt-1 text-xs text-slate-500">No active alerts or compliance issues.</p></div>
            </div>
          </DashboardCard>
        </section>

        <section class="mb-5 grid gap-5 xl:grid-cols-12">
          <DashboardCard class="xl:col-span-5" title="Money" subtitle="Receivables and cash flow" action-label="Open Payments" @action="go('/payments')">
            <div class="grid gap-3 sm:grid-cols-3">
              <div class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4"><span class="text-xs text-slate-500">Monthly Earnings</span><strong class="mt-1 block text-xl text-white">{{ money(stats.monthly_earnings) }}</strong></div>
              <div class="rounded-xl border border-amber-500/15 bg-amber-500/[.05] p-4"><span class="text-xs text-slate-500">Pending Payments</span><strong class="mt-1 block text-xl text-amber-300">{{ stats.pending_payments ?? 0 }}</strong></div>
              <div class="rounded-xl border border-red-500/15 bg-red-500/[.04] p-4"><span class="text-xs text-slate-500">Unpaid Invoices</span><strong class="mt-1 block text-xl text-red-300">{{ stats.unpaid_invoices ?? 0 }}</strong></div>
            </div>
            <div class="mt-4 space-y-2">
              <QueueRow v-for="(item, i) in (data.invoices || []).slice(0, 4)" :key="item.id || i" :title="item.number || item.reference || `Invoice ${i + 1}`" :subtitle="item.customer || item.description || 'Invoice'" :meta="money(item.amount)" :tone="item.status === 'paid' ? 'emerald' : item.status === 'overdue' ? 'red' : 'amber'" />
              <div v-if="!data.invoices?.length" class="rounded-xl border border-dashed border-slate-700/60 p-4 text-center text-sm text-slate-500">No invoice activity yet.</div>
            </div>
          </DashboardCard>

          <DashboardCard class="xl:col-span-4" title="Upcoming Dispatch" subtitle="Next field assignments" action-label="My Dispatch" @action="go('/dispatch')">
            <div class="space-y-2">
              <QueueRow v-for="(item, i) in (data.dispatches || []).slice(0, 5)" :key="item.id || i" :title="item.title || item.service_type || 'Dispatch'" :subtitle="[item.well_name, item.location].filter(Boolean).join(' · ')" :meta="item.time || item.start_time || item.date || ''" tone="emerald" />
              <div v-if="!data.dispatches?.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">No dispatches scheduled.</div>
            </div>
          </DashboardCard>

          <DashboardCard class="xl:col-span-3" title="Field Tickets" subtitle="Work ready to close" action-label="Open Tickets" @action="go('/field-tickets')">
            <div class="space-y-2">
              <QueueRow v-for="(item, i) in (data.tickets || []).slice(0, 5)" :key="item.id || i" :title="item.ticket_number || item.number || `Ticket ${i + 1}`" :subtitle="item.well_name || item.customer || item.service_type || ''" :meta="item.status || ''" :tone="item.status === 'approved' ? 'emerald' : item.status === 'rejected' ? 'red' : 'purple'" />
              <div v-if="!data.tickets?.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">No open field tickets.</div>
            </div>
          </DashboardCard>
        </section>

        <section class="grid gap-5 xl:grid-cols-12">
          <DashboardCard class="xl:col-span-4" title="Safety & Certifications" subtitle="Stay job-ready" action-label="Open Safety" @action="go('/safety')">
            <div class="mb-4 flex items-center gap-4 rounded-xl border border-emerald-500/15 bg-emerald-500/[.05] p-4">
              <div class="grid h-16 w-16 place-items-center rounded-full border-8 border-emerald-500/25 text-lg font-black text-emerald-300">{{ stats.safety_score ?? 100 }}%</div>
              <div><p class="font-bold text-white">Safety Score</p><p class="mt-1 text-xs text-slate-500">Driven by real API values only.</p></div>
            </div>
            <div class="space-y-2">
              <QueueRow v-for="(item, i) in (data.certifications || []).slice(0, 4)" :key="item.id || i" :title="item.name || item.certification || 'Certification'" :subtitle="item.expires_at ? `Expires ${item.expires_at}` : item.status || ''" :meta="item.days_remaining != null ? `${item.days_remaining} days` : ''" :tone="item.days_remaining != null && item.days_remaining <= 30 ? 'amber' : 'emerald'" />
              <div v-if="!data.certifications?.length" class="rounded-xl border border-dashed border-slate-700/60 p-4 text-center text-sm text-slate-500">Certification dates will appear here.</div>
            </div>
          </DashboardCard>

          <DashboardCard class="xl:col-span-8" title="Recent Activity" subtitle="Latest work and payment updates">
            <div class="space-y-2">
              <QueueRow v-for="(item, i) in (data.activity || []).slice(0, 7)" :key="item.id || i" :title="item.title || item.description || 'Activity'" :subtitle="item.description && item.title ? item.description : item.location || ''" :meta="item.time || item.created_at || item.timestamp || ''" :tone="item.status === 'success' ? 'emerald' : item.status === 'warning' ? 'amber' : 'cyan'" />
              <div v-if="!data.activity?.length" class="grid min-h-60 place-items-center rounded-xl border border-dashed border-slate-700/60 p-6 text-center">
                <div><div class="text-2xl">▣</div><p class="mt-2 font-semibold text-white">No recent activity</p><p class="mt-1 text-sm text-slate-500">Your completed actions and updates will appear here.</p></div>
              </div>
            </div>
          </DashboardCard>
        </section>
      </template>
    </div>
  </AppLayout>
</template>
